/*
 * Copyright (c) 2025 Ipivot Corporation.
 * All rights reserved.
 * This software is the confidential and proprietary information of
 *  Ipivot Corporation ("Confidential Information").
 */
import Container from 'react-bootstrap/Container';
import { Row, Col } from 'react-bootstrap';
import Card from 'react-bootstrap/Card';
import Spinner from 'react-bootstrap/Spinner';
import { useState, useEffect, useContext, useCallback, useRef } from 'react';
import { getProbeChildren, getProbeRoots } from '../data';
import { WsContext, WsContextType } from './WsContext';
import { useTheme } from 'styled-components';
import ProbeTree from './ProbeTree';

function ProbeRootsPage() {
    const [forests, setForests] = useState<Map<number, any[]>>(new Map());
    const [lastActiveId, setLastActiveId] = useState<number | null>(null);
    const [initialCollapsed, setInitialCollapsed] = useState<Set<number> | null>(null);
    const wsContext = useContext(WsContext) as WsContextType;
    const theme: any = useTheme();

    const addTree = useCallback(async (rootId: number) => {
        try {
            const children = await getProbeChildren(rootId);
            setForests(prev => new Map(prev).set(rootId, children));
        } catch (e: any) {
            console.error('Error fetching probe children for root', rootId, e);
        }
    }, []);

    // Load only roots on mount; fold all by default for lazy loading
    useEffect(() => {
        getProbeRoots().then(async roots => {
            if (!roots.length) {
                setInitialCollapsed(new Set());
                return;
            }
            // Initialize forests with root node itself (no children yet) — fetch children on expand
            const entries = roots.map(r => [r.id, [
                {
                    id: r.id,
                    parent_id: null,
                    probe_type: r.probe_type,
                    passed: r.passed,
                    pending: r.pending,
                }
            ]] as [number, any[]]);
            setForests(new Map(entries));
            setInitialCollapsed(new Set(roots.map(r => r.id)));
        }).catch(e => console.error('Error fetching probe roots', e));
    }, []);

    // Mirror forests in a ref so the WS handler can read current state
    // without capturing a stale closure.
    const forestsRef = useRef<Map<number, any[]>>(new Map());
    useEffect(() => { forestsRef.current = forests; }, [forests]);

    // Subscribe to WS events
    useEffect(() => {
        if (!wsContext.ws) return;
        const onProbeLifecycle = (msg: any) => {
            const { rootId, id, running, passed, pending } = msg;
            if (!rootId || !id) return;
            if (running) setLastActiveId(id);

            const nodes = forestsRef.current.get(rootId);
            if (!nodes) {
                // Unknown root — fetch the whole tree immediately
                addTree(rootId);
                return;
            }
            const idx = nodes.findIndex((n: any) => n.id === id);
            if (idx === -1) {
                // New node in a known tree — fetch updated tree immediately
                addTree(rootId);
                return;
            }
            // Patch the node's state in-place — no fetch needed
            const updated = [...nodes];
            updated[idx] = { ...updated[idx], running, passed, pending };
            setForests(prev => new Map(prev).set(rootId, updated));
        };
        wsContext.ws.on('probeLifecycle', onProbeLifecycle);
        wsContext.ws.emit('refresh', {});
        return () => {
            wsContext.ws.off('probeLifecycle', onProbeLifecycle);
        };
    }, [wsContext, addTree]);

    return (
        <Container fluid style={{ backgroundColor: theme.colors.bg, color: theme.colors.text, height: '100%', display: 'flex', flexDirection: 'column', padding: 0 }}>
            <Row style={{ flex: 1, overflow: 'hidden', margin: '0.25rem' }}>
                <Col style={{ padding: 0, height: '100%' }}>
                    <Card style={{
                        height: '100%',
                        overflow: 'hidden',
                        display: 'flex',
                        flexDirection: 'column',
                        backgroundColor: theme.colors.bg2 || theme.colors.bg,
                        borderColor: theme.colors.border || theme.colors.ctext,
                    }}>
                        <Card.Header style={{ fontFamily: 'monospace', color: theme.colors.ctext }}>
                            Probe Roots
                        </Card.Header>
                        <Card.Body style={{ padding: 0, flex: 1, minHeight: 0, overflow: 'hidden', position: 'relative' }}>
                            {initialCollapsed === null ? (
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                                    <Spinner animation="border" variant="secondary" />
                                </div>
                            ) : (
                                <ProbeTree
                                    forests={forests}
                                    lastActiveId={lastActiveId}
                                    initialCollapsed={initialCollapsed}
                                    initialStick={false}
                                    onExpandNode={addTree}
                                />
                            )}
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        </Container>
    );
}

export default ProbeRootsPage;
